// src/contexts/AuthContext.tsx
import {createContext, useState, useEffect, useContext } from 'react';
import api from '../api/client';

interface User { id: string; username: string; }
interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (username: string, password: string) => Promise<void>;
    loginAdmin: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // On mount, check if cookie is valid
    useEffect(() => {
        api.get<User>('/Login/me')
            .then(res => setUser(res.data))
            .catch(() => setUser(null))
            .finally(() => setLoading(false));
    }, []);

    const login = async (username: string, password: string) => {
        await api.post('/Login/user', { username, password });
        // backend set-cookie ⇒ now refresh user info
        const res = await api.get<User>('/Login/me');
        setUser(res.data);
    };

    const loginAdmin = async (username: string, password: string) => {
        await api.post('/login/admin', { username, password });
        // backend set-cookie ⇒ now refresh user info
        const res = await api.get<User>('/login/me');
        setUser(res.data);
    };

    const logout = async () => {
        await api.post('/login/logout'); // clear cookie server-side
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, loginAdmin, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be inside AuthProvider');
    return ctx;
}
